import { CircularProgress } from '@mui/material';
import TooltipRedirectLink from 'components/V2/TooltipRedirectLink';

import { ReactComponent as DownloadIcon } from 'asset/IconsPack/hx_download-01.svg';
import CheckInProgress from 'asset/img/check_in_progress.svg';
import NoRecordsFound from 'asset/img/no_records_found_v2.svg';

export const VERIFICATION_RESULT_TOOLTIP = {
    TWO_FA: 'Two-factor authentication (2FA) strengthens security by requiring two forms of verification, like a one-time passcode sent to both email and phone, reducing the risk of unauthorized access.',
    GOVT_ID: (
        <>
            Verify a Government Photo ID, read with OCR & where the barcode is decoded and matched
            with the data from the document. We also provide our degree of confidence that it’s
            authentic.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/government-id-verification" />
        </>
    ),
    VERIFY_QUIZ: (
        <>
            Verify & authenticate identity with a personal knowledge quiz generated from the user’s
            name, date of birth and occasionally an address. In rare cases a partial or full SSN is
            required.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/knowledge-based-authentication" />
        </>
    ),
    FAOV: (
        <>
            Authenticate a user’s identity by connecting their financial account, and matching the
            registered names. For higher confidence, we recommend enabling the authoritative
            database check as well.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/financial-account-ownership-verification-usa" />
        </>
    ),
    SEVEN_YEAR_CRIMINAL_ACTIVITY: (
        <>
            Verify any commercially available criminal activity data that can be found nationwide
            for the last seven years. No pass through fees & an instant turnaround.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/7-year-criminal-report-usa" />
        </>
    ),
    SEX_OFFENDER_CHECK_PRO: (
        <>
            Sex Offender Check Pro helps organizations identify individuals with a history of sexual
            offenses by leveraging an extensive database of over 600,000 records, including level 1
            offenses. Unlike traditional search tools, it uses advanced techniques to verify and
            enhance incomplete data, ensuring more accurate results. With superior matching
            capabilities, it minimizes false positives and false negatives, providing timely and
            actionable insights for informed decision-making. If a match is found, a detailed report
            is available.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/sex-offender-check" />
        </>
    ),
    GLOBAL_WATCHLIST_CHECK: (
        <>
            The Global Watch List Search helps identify if a person is flagged on any U.S. or
            international sanctions and watch lists, like OFAC or the UN list. If a match is found,
            a detailed report is available.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/global-watchlist-report" />
        </>
    ),
    TRUE_CONTINUOUS_MONITORING:
        'Provides alerts in near-real time about new criminal activity that could indicate threats to your business.',

    EDUCATION: (
        <>
            Verify School Name & Type, Campus Name, School Town & Country, Start Date & End Date,
            Degree Title, if they are currently attending and/or if they've completed their degree
            successfully - no passthrough fees.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/education-verification" />
        </>
    ),
    EMPLOYMENT: (
        <>
            Verify Employer Name, Town, State & Country, Job Title, Date Employed From & To,
            Supervisor Name, if they are currently employed & what their contract type is - no
            passthrough fees. If unavailable by database the average turnaround time is 48 hours to
            get a response from the source.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/employment-verification" />
        </>
    ),
    PROFESSIONAL_LICENSE: (
        <>
            Verify License Title, Organization & Number, Start & End Date, State & Country. If
            unavailable by database the average turnaround time is 48 hours to get a response from
            the source.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/professional-license-verification" />
        </>
    ),
    RISK_SCORE: (
        <>
            User insights from Risk Score involve understanding risk levels, engagement across
            channels, behavioral patterns, and user segmentation based on activity scores. Higher
            confidence scores typically indicate lower risk, aiding organizations in making informed
            decisions. <TooltipRedirectLink url="https://info.authenticate.com/score-inputs" />
        </>
    ),
    RISK_INSIGHTS: (
        <>
            Risk Insights provides input data insights into how confident we are about the given
            person's identity, inputs, and risk level. Risk Insights are highly specific and provide
            high-value information. Our identity graph spans both the digital and terrestrial
            ecosystems and provides linkages between how individuals represent themselves
            professionally and personally.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/verify-inputs" />
        </>
    ),
    'County Criminal': (
        <>
            County Criminal Record Verification retrieves felony and misdemeanor records from all
            counties where an individual has resided, delivering accurate local criminal history
            insights. This service pulls official data from county-level courts to help businesses
            assess risk with detailed criminal background information.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/county-criminal-civil-record-verification-usa" />
        </>
    ),
    'Federal Criminal': (
        <>
            Federal Criminal Record Verification automatically searches all publicly available
            federal criminal records to uncover offenses prosecuted in U.S. federal courts. This
            service highlights federal-level crimes, including violations of federal statutes and
            other serious offenses, for comprehensive risk assessment. Verified results are
            typically delivered within 24 hours, supporting secure and informed business decisions.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/federal-civil-criminal-record-verification-usa" />
        </>
    ),
    'Federal Civil': (
        <>
            Federal Civil Record Verification retrieves all accessible federal civil court records
            related to an individual, providing thorough case history coverage. By screening federal
            civil filings alongside criminal data, this service delivers a complete view of an
            individual’s federal litigation history. With fast turnaround times and authoritative
            sources, businesses can confidently assess risk and maintain compliance.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/federal-civil-criminal-record-verification-usa" />
        </>
    ),
    'County Civil': (
        <>
            County Civil Record Verification searches upper and lower court records for civil cases,
            covering claims both below and above state-specific monetary thresholds. This check
            reveals non-criminal legal disputes and case details from county courts to support
            comprehensive background insights.{' '}
            <TooltipRedirectLink url="https://info.authenticate.com/county-criminal-civil-record-verification-usa" />
        </>
    ),
    MVR: (
        <>
            Motor Vehicle Record Verification provides direct access to official DMV driving
            records. Verify license status, issuance and expiration dates, and reported violations.
            All information is sourced directly from state DMVs to ensure maximum accuracy and
            reliability. Make confident, risk-aware decisions with up-to-date driving record
            insights. <TooltipRedirectLink url="https://info.authenticate.com/dmv-mvr" />
        </>
    ),
};

export const USER_PROFILE_TAB = {
    OVERVIEW: 'overview',
    '2FA_RISK_ASSESSMENT': '2fa-risk-assessment',
    IDENTITY: 'identity',
    CRIMINAL_CHECKS: 'criminal-checks',
    CIVIL_CHECKS: 'civil-checks',
    BACKGROUND_CHECKS: 'background-checks',
    EVENTS: 'events',
};

export const USER_INFO_FORM_FIELDS = {
    EMAIL: 'email',
    PHONE_NUMBER: 'phone',
    FIRST_NAME: 'firstName',
    MIDDLE_NAME: 'middleName',
    LAST_NAME: 'lastName',
    DOB: 'dob',
    SSN: 'ssn',
    HOUSE_NUMBER: 'houseNumber',
    STREET_NAME: 'streetName',
    APARTMENT_NUMBER: 'apartmentNumber',
    ZIP_CODE: 'zipCode',
    COUNTRY: 'country',
    ADDRESS: 'address',
    CITY: 'city',
    STATE: 'state',
    ZIP: 'zip',
};

export const USER_INFO_FORM_FIELDS_LABELS = {
    [USER_INFO_FORM_FIELDS.FIRST_NAME]: 'First Name',
    [USER_INFO_FORM_FIELDS.MIDDLE_NAME]: 'Middle Name',
    [USER_INFO_FORM_FIELDS.LAST_NAME]: 'Last Name',
    [USER_INFO_FORM_FIELDS.DOB]: 'DOB',
    [USER_INFO_FORM_FIELDS.SSN]: 'SSN',
    [USER_INFO_FORM_FIELDS.HOUSE_NUMBER]: 'House Number',
    [USER_INFO_FORM_FIELDS.STREET_NAME]: 'Street Name',
    [USER_INFO_FORM_FIELDS.APARTMENT_NUMBER]: 'Apartment Number',
    [USER_INFO_FORM_FIELDS.ZIP_CODE]: 'Zip Code',
    [USER_INFO_FORM_FIELDS.COUNTRY]: 'Country',
    [USER_INFO_FORM_FIELDS.CITY]: 'City',
    [USER_INFO_FORM_FIELDS.STATE]: 'State',
    [USER_INFO_FORM_FIELDS.STREET_NAME]: 'Street Name',
};
export const USER_PROFILE_TAB_LABELS = [
    {
        label: 'Overview',
        value: USER_PROFILE_TAB.OVERVIEW,
    },
    {
        label: '2FA & Risk Assessment',
        value: USER_PROFILE_TAB['2FA_RISK_ASSESSMENT'],
    },
    {
        label: 'Identity',
        value: USER_PROFILE_TAB.IDENTITY,
    },
    {
        label: 'Criminal Checks',
        value: USER_PROFILE_TAB.CRIMINAL_CHECKS,
    },
    {
        label: 'Civil Checks',
        value: USER_PROFILE_TAB.CIVIL_CHECKS,
    },
    {
        label: 'Background Verifications',
        value: USER_PROFILE_TAB.BACKGROUND_CHECKS,
    },
    {
        label: 'Events',
        value: USER_PROFILE_TAB.EVENTS,
    },
];

export const RESULT_VERIFICATION_STATUS = {
    DEFAULT: 'default',
    SUCCESS: 'success',
    PENDING: 'pending',
    FAILED: 'failed',
};

export const verificationStatusBoxShadow = {
    [RESULT_VERIFICATION_STATUS.DEFAULT]: 'none',
    [RESULT_VERIFICATION_STATUS.SUCCESS]: '0px 0px 5px 0px #32D583',
    [RESULT_VERIFICATION_STATUS.PENDING]: '0px 0px 5px 0px #FEC84B',
    [RESULT_VERIFICATION_STATUS.FAILED]: '0px 0px 5px 0px #D92D20',
};

export const EMPTY_RECORDS_UI_MESSAGES = {
    success: 'No records found yet.',
    pending: 'The check is still ongoing, and results are pending.',
};

export const EMPTY_RECORDS_UI_ICONS = {
    success: NoRecordsFound,
    pending: CheckInProgress,
};

export const STATUS_INDICATOR_MESSAGES = {
    success: 'Clear',
    pending: 'In Progress',
    failed: 'Records Found',
};

export const RECORDS_VALUE_TYPE = {
    DEFAULT: 'default',
    NULL: 'null',
    POSITIVE: 'success',
    NEGATIVE: 'failed',
    LINK: 'link',
};

export const BASE_URL =
    process.env.REACT_APP_BUILD_ENV === 'production'
        ? 'https://verify.authenticating.com'
        : 'https://verify-stage.authenticating.com';

export const MAP_TWO_FA_RESULT_KEYS = {
    email: 'Email',
    email_verified: 'Verified',
    phone: 'Phone Number',
    phone_verified: 'Verified',
};

// Govt ID results that mean the scan finished but did not pass, with their display labels
export const GOVT_ID_FAILED_RESULT_LABELS = {
    failed: 'Failed',
    tamper_check_failed: 'Tamper Check Failed',
    id_expired: 'ID Expired',
    invalid_dob: 'Invalid DOB',
    invalid_name: 'Invalid Name',
    restricted_doc_type: 'Restricted Document Type',
    restricted_country: 'Restricted Country',
    capture_quality_failed: 'Capture Quality Failed',
    document_front_not_readable: 'Front Not Readable',
    back_image_required: 'Back Image Required',
    back_image_not_readable: 'Back Not Readable',
    document_data_mismatch: 'Document Data Mismatch',
    document_liveness_failed: 'Document Liveness Failed',
    authenticity_check_failed: 'Authenticity Check Failed',
    parsing_failed: 'Parsing Failed',
    unknown_error: 'Unknown Error',
};

export const MAP_BOOLEAN_RESULT_VALUES = {
    true: 'TRUE',
    false: 'FALSE',
    null: 'NA',
    complete: 'Complete',
    incomplete: 'Incomplete',
    passed: 'Passed',
    PoorQuality: 'Poor Quality',
    ...GOVT_ID_FAILED_RESULT_LABELS,
};

export const MAP_IDENTITY_RESULT_KEYS = {
    name: 'Name',
    name_verified: 'Verified',
    dob: 'DOB',
    dob_verified: 'Verified',
    ssn: 'SSN',
    ssn_verified: 'Verified',
};

export const MAP_QUIZ_RESULT_KEYS = {
    totalQuestions: 'Number of Questions',
    score: 'Quiz Score',
    verification_status: 'Verification Status',
};

export const MAP_GOVT_ID_VERIFICATION_KEYS = {
    document_type: 'Document Type',
    attempts: 'Attempts',
    result: 'Result',
    confidence_rating: 'Confidence Rating',
    authentication_result: 'Authentication Result',
    firstname: 'First Name',
    middlename: 'Middle Name',
    lastname: 'Last Name',
    first_name: 'First Name',
    middle_name: 'Middle Name',
    last_name: 'Last Name',
    address: 'Address',
    dob: 'DOB',
    gender: 'Gender',
    passport_number: 'Passport Number',
    id_issued_date: 'ID Issued Date',
    id_expiry_date: 'ID Expiry Date',
    id_expiration_date: 'ID Expiry Date',
    id_number: 'ID Number',
    nationality: 'Nationality',
    id_type: 'ID Type',
    method: 'Method',
    face_match_score: 'Face Match Score',
    liveness_error: 'Liveness Error',
    liveness_score: 'Liveness Score',
    id_class: 'ID Class',
    livenessAssessment: 'Liveness Assessment',
};

export const EEP_FORM_TYPE = {
    EDUCATION: 'education',
    EMPLOYMENT: 'employment',
    PROFESSIONAL_LICENSE: 'professional_license',
};

export const EEP_FORM_INFO = {
    [EEP_FORM_TYPE.EDUCATION]: {
        title: 'Education',
        id: 'education',
        tooltipContent: VERIFICATION_RESULT_TOOLTIP.EDUCATION,
        titleSuffix: 'institutionName',
    },
    [EEP_FORM_TYPE.EMPLOYMENT]: {
        title: 'Employment',
        id: 'employment',
        tooltipContent: VERIFICATION_RESULT_TOOLTIP.EMPLOYMENT,
        titleSuffix: 'employerName',
    },
    [EEP_FORM_TYPE.PROFESSIONAL_LICENSE]: {
        title: 'Professional License',
        id: 'professional_license',
        tooltipContent: VERIFICATION_RESULT_TOOLTIP.PROFESSIONAL_LICENSE,
        titleSuffix: 'license_title',
    },
};

export const RECORD_STATUS = {
    COMPLETE: 'COMPLETE',
    PENDING: 'PENDING',
    CLEAR: 'CLEAR',
    RECORD: 'RECORD',
    UNDER_REVIEW: 'UNDER_REVIEW',
};

export const eepFilterKeys = new Set(['status', 'manual_verification', null]);

export const EXTENDED_CHECKS_TYPES = {
    CIVIL_COUNTY: 'County Civil',
    CRIMINAL: 'County Criminal',
    CRIMINAL_FEDERAL: 'Federal Criminal',
    CIVIL_FEDERAL: 'Federal Civil',
};

// Common keys for SOC and County/Federal checks case details
export const filteredCaseDetailsKeys = new Set([
    'caseNumber',
    'courtFiledIn',
    'countyFiledIn',
    'chargeDate',
    'offenseDate',
    'dispositionDate',
]);

// Common keys for County/Federal checks subject details
export const filteredSubjectDetailsKeys = new Set([
    'name',
    'isNameMatch',
    'firstName',
    'isFirstNameMatch',
    'middleName',
    'isMiddleNameMatch',
    'lastName',
    'isLastNameMatch',
    'birthdate',
    'isBirthdateMatch',
    'address',
    'isAddressMatch',
    'isSSNMatch',
    'isAliasMatch',
]);

// Keys to omit from document verification result
export const filteredDocumentResultKeys = new Set([
    'watermarkedImages',
    'id_back',
    'id_front',
    'face_image',
    'signature_image',
    'selfie_image',
    'method',
]);

export const ID_VERIFICATION_CTA_TYPE = {
    GOVT_ID: 'govt_id',
    KBA: 'kba',
    BANK: 'bank',
};

export const CRIMINAL_CHECKS_CTA_TYPE = {
    SEVEN_YEAR_CHECK: 'seven_year_criminal_report',
    SOC_CHECK: 'sex_offender',
    GWC_CHECK: 'global_watchlist',
    COUNTY_CRIMINAL_CHECK: 'criminal',
    COUNTY_CIVIL_CHECK: 'civil_county',
    FEDERAL_CRIMINAL_CHECK: 'criminal_federal',
    FEDERAL_CIVIL_CHECK: 'civil_federal',
};

export const ID_VERIFICATION_CTA_STATUS = {
    ADDED_TO_WORKFLOW: 'added_to_workflow',
    NOT_ATTEMPTED: 'not_attempted',
    ATTEMPTED: 'attempted',
    OTHER_INFORMATION: 'other_information',
    COMPLETED: 'completed',
};

export const CIVIL_CHECK_TYPES = ['CIVIL_COUNTY', 'CIVIL_FEDERAL'];

export const ALERT_MODAL_TYPE = {
    LOW_BALANCE: 'low balance',
    COUNTY_EXISTS: 'county exists',
    FEDERAL_CIVIL_CHECK: 'federal civil',
    FEDERAL_CRIMINAL_CHECK: 'federal criminal',
    COUNTY_CRIMINAL_CHECK_CHARGE: 'county criminal',
    COUNTY_CIVIL_CHECK_CHARGE: 'county civil',
    GWC: 'gwc',
    SOC: 'soc',
    SEVEN_YEAR_CHECK: 'seven year',
};

export const eepFormTitle = {
    educationInfo: 'Education',
    employerInfo: 'Employer',
    licenseInfo: 'Professional License',
};

export const jobTypes = [
    { label: 'Full Time Employee', value: 'full time employee' },
    { label: 'Part Time Employee', value: 'part time employee ' },
    { label: 'Contractor', value: 'contractor' },
    { label: 'Internship', value: 'internship' },
    { label: 'Temporary', value: 'temporary' },
    { label: 'Commission', value: 'commission' },
];

export const EEP_MODAL_TYPE = {
    EDUCATION: 'education',
    EMPLOYMENT: 'employment',
    PROFESSIONAL_LICENSE: 'license',
};

export const EEP_FORM_FIELD_NAME = {
    EDUCATION: 'educationInfo',
    EMPLOYMENT: 'employerInfo',
    PROFESSIONAL_LICENSE: 'licenseInfo',
};

export const EEP_HEADER_FOOTER_TEXT = {
    [EEP_FORM_FIELD_NAME.EDUCATION]: {
        title: 'Education Verification',
        btnText: 'Add Education',
    },
    [EEP_FORM_FIELD_NAME.EMPLOYMENT]: {
        title: 'Employment Verification',
        btnText: 'Add Employer',
    },
    [EEP_FORM_FIELD_NAME.PROFESSIONAL_LICENSE]: {
        title: 'Professional License Verification',
        btnText: 'Add License',
    },
};

export const DATE_PICKER_TITLE = {
    [EEP_FORM_FIELD_NAME.EDUCATION]: {
        startDate: 'Start *',
        endDate: 'End *',
        checkbox: 'Currently Attending',
    },
    [EEP_FORM_FIELD_NAME.EMPLOYMENT]: {
        startDate: 'Start *',
        endDate: 'End *',
        checkbox: 'Currently Employed',
    },
    [EEP_FORM_FIELD_NAME.PROFESSIONAL_LICENSE]: {
        startDate: 'Issue Date *',
        endDate: 'Expiration Date *',
        checkbox: false,
    },
};

export const SYNC_USER_INFO_FIELDS = [
    USER_INFO_FORM_FIELDS.HOUSE_NUMBER,
    USER_INFO_FORM_FIELDS.STREET_NAME,
    USER_INFO_FORM_FIELDS.CITY,
    USER_INFO_FORM_FIELDS.STATE,
    USER_INFO_FORM_FIELDS.ZIP_CODE,
    USER_INFO_FORM_FIELDS.COUNTRY,
];

const SEVEN_YEAR_CHECK_ADDRESS_FIELDS = [
    'City',
    'State',
    'Zipcode',
    'Country',
    'House Number',
    'Street Name',
];

export const SEVEN_YEAR_CHECK_MISSING_ADDRESS_ERROR_MESSAGES = SEVEN_YEAR_CHECK_ADDRESS_FIELDS.map(
    (field) =>
        `${field} required to run this check. Please update the user information and try again.`
);

export const SEVEN_YEAR_CHECK_ALL_FIELDS_REQUIRED_ERROR =
    'House Number, Street Name, City, State and Zipcode are required to run this check. Please update the user information and try again.';

export const SEVEN_YEAR_CHECK_MISSING_SSN_ERROR_MESSAGES = [
    'SSN is required to run this check. Please update the user information and try again.',
    'Full SSN is required to run this check. Please update the user information and try again.',
];

export const SEVEN_YEAR_RECORD_MESSAGES = {
    NO_RECORDS_FOUND: 'No Criminal Records Found',
    CRIMINAL_RECORDS_FOUND: 'Criminal Records Found',
    UNDER_REVIEW: 'Under Review',
};

export const GWC_RECORD_MESSAGES = {
    MATCH_FOUND: 'Match Found',
    NO_MATCH_FOUND: 'No Match Found',
};

export const FEDERAL_CHECK_PRICE = 6.5;
export const GWC_CHECK_PRICE = 1;
export const SEVEN_YEAR_CHECK_PRICE = 5;

export const UPLOADED_IMAGE_STYLES = {
    mobile: {
        idCardStyles: {
            width: 230,
            height: 'auto',
            aspectRatio: 16 / 9,
        },
        otherImageStyles: {
            width: 135,
            height: 180,
            aspectRatio: 3 / 4,
        },
    },
    tablet: {
        idCardStyles: {
            width: 320,
            height: 200,
            aspectRatio: 16 / 9,
        },
        otherImageStyles: {
            width: 160,
            height: 215,
            aspectRatio: 3 / 4,
        },
    },
    desktop: {
        idCardStyles: {
            width: 400,
            height: 250,
            aspectRatio: 16 / 9,
        },
        otherImageStyles: {
            width: 191,
            height: 250,
            aspectRatio: 3 / 4,
        },
    },
};

export const ADDON_CHECK_TOOLTIP_MESSAGES = {
    IDENTITY_NOT_VERIFIED: 'User identity verification is pending.',
    IDENTITY_VERIFIED: 'User identity is already verified.',
    VERIFICATION_COMPLETED: 'This check is already completed.',
    ALREADY_ADDED_TO_WORKFLOW: 'Check is already added to the workflow.',
    SSN_ALREADY_VERIFIED: 'SSN is already verified.',
    CHECK_ALREADY_RUN: 'This check has already been run for this user.',
    IN_PROGRESS: 'This check is in progress.',
    RISK_SCORE_NOT_COMPLETED: 'Risk Score must be run before initiating Risk Insights.',
    DOCUMENT_COUNTRY_NOT_US: 'This check is not available for non-US documents.',
};

export const INTERNAL_S3_URL = 'https://authenticating-assets-dev.s3.amazonaws.com/';

export const REFETCH_RESULTS_KEYS = {
    EDUCATION: 'education',
    EMPLOYMENT: 'employment',
    PROFESSIONAL_LICENSE: 'professionalLicense',
    MVR: 'mvr',
    SOC: 'soc',
};

export const MEDALLION_SESSION_INFO_TYPE = {
    WORKFLOW_ASSIGNED: 'WORKFLOW_ASSIGNED',
    WORKFLOW_REASSIGNED: 'WORKFLOW_REASSIGNED',
    CHECK_ADDED: 'CHECK_ADDED',
    LINK_GENERATED: 'LINK_GENERATED',
};

export const LINK_DESCRIPTION_VARIANT = {
    ERROR: 'error',
    INFO: 'info',
    WARNING: 'warning',
    DEFAULT: 'default',
};

export const MEDALLION_LINK_DESCRIPTIONS = {
    [MEDALLION_SESSION_INFO_TYPE.LINK_GENERATED]: {
        text: <strong>New link has been generated, resend link to verify.</strong>,
        variant: LINK_DESCRIPTION_VARIANT.INFO,
    },
    [MEDALLION_SESSION_INFO_TYPE.WORKFLOW_ASSIGNED]: {
        text: <strong>Workflow has been added, send link to verify.</strong>,
        variant: LINK_DESCRIPTION_VARIANT.INFO,
    },
    [MEDALLION_SESSION_INFO_TYPE.WORKFLOW_REASSIGNED]: {
        text: <strong>Workflow has been reassigned, resend link to verify</strong>,
        variant: LINK_DESCRIPTION_VARIANT.INFO,
    },
    [MEDALLION_SESSION_INFO_TYPE.CHECK_ADDED]: {
        text: <strong>New check has been added to medallion, resend link to verify</strong>,
        variant: LINK_DESCRIPTION_VARIANT.WARNING,
    },
};

export const US_COUNTRY_VARIANTS = ['united states', 'united states of america', 'us', 'usa'];

export const USER_DOCUMENT_TYPES = {
    GOVT_ID: 'govt-id',
    PASSPORT: 'passport',
};

export const USER_DOCUMENT_RESULT_FIELDS = {
    RESULT: 'Result',
    AUTHENTICATION_RESULT: 'Authentication Result',
    CONFIDENCE_RATING: 'Confidence Rating',
};

export const USER_DOCUMENT_FAILED_VALUES = {
    TAMPER_CHECK_FAILED: 'tamper_check_failed',
    FAILED: 'Failed',
};

export const USER_DOCUMENT_FAILED_MODAL_TYPES = {
    TAMPER_CHECK_FAILED: 'TAMPER_CHECK_FAILED',
    FAILED: 'FAILED',
    CONFIDENCE_SCORE_LOW: 'CONFIDENCE_SCORE_LOW',
};

export const USER_DOCUMENT_FAILED_MODAL_TITLES = {
    [USER_DOCUMENT_FAILED_MODAL_TYPES.TAMPER_CHECK_FAILED]: 'What does Tamper Checks mean?',
    [USER_DOCUMENT_FAILED_MODAL_TYPES.FAILED]: 'What does Failed mean?',
    [USER_DOCUMENT_FAILED_MODAL_TYPES.CONFIDENCE_SCORE_LOW]: 'When Confidence Score Is Below 75%',
};

// Migrated from old UserProfile
export const TWO_FA_NUDGE_STATUES = {
    PENDING: 'jwt_nudge',
    SUCCESS: 'success',
    WARNING: 'warning',
    FAIL: 'failure',
};

export const TWO_FA_NUDGE_STATES = {
    MISSING_EMAIL: 'MISSING_EMAIL',
    MISSING_PHONE: 'MISSING_PHONE',
    MISSING_EMAIL_AND_PHONE: 'MISSING_EMAIL_AND_PHONE',
    EMAIL_AND_PHONE_SUCCESS: 'EMAIL_AND_PHONE_SUCCESS',
    EMAIL_SUCCESS: 'EMAIL_SUCCESS',
    PHONE_SUCCESS: 'PHONE_SUCCESS',
    EMAIL_FAIL: 'EMAIL_FAIL',
    PHONE_FAIL: 'PHONE_FAIL',
    EMAIL_AND_PHONE_FAIL: 'EMAIL_AND_PHONE_FAIL',
};

export const WORKFLOW_STATUS_CODES = {
    PENDING: 'PENDING',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
};

export const INVALID_SSN_MESSAGE =
    "We could not verify the SSN. Please review the SSN and the user's PII and make sure it is accurate.";

export const GOVT_ID_API_IMAGE_KEY_MAP = {
    ID_FRONT: 'id_front',
    ID_BACK: 'id_back',
    SELFIE: 'selfie_image',
    SIGNATURE: 'signature_image',
    FACE: 'face_image',
};

export const GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP = {
    ID_FRONT: 'idFront',
    ID_BACK: 'idBack',
    SELFIE: 'selfie',
    SIGNATURE: 'signature',
    FACE: 'face',
};

export const SECTION_LOADING_KEYS = {
    ALL_SECTIONS: 'allSections',
    USER_CONSENT: 'userConsent',
    EDIT_USER_INFO: 'editUserInfo',
    VERIFICATION_LINK: 'verificationLink',
    WORKFLOW_OVERVIEW: 'workflowOverview',
    TWO_FA_AND_RISK_TAB: 'twoFaAndRisk',
    TWO_FA_AND_RISK_RESULT: 'twoFaAndRiskResult',
    RISK_SCORE_RUN: 'riskScoreRun',
    RISK_INSIGHTS_RUN: 'riskInsightsRun',
    IDENTITY_TAB: 'identity',
    AUTH_DB_CHECK_RUN: 'authDbCheckRun',
    SSN_VERIFY_RUN: 'ssnVerifyRun',
    CRIMINAL_CHECKS_TAB: 'criminalChecks',
    CRIMINAL_CHECKS_INIT: 'criminalChecksInit',
    CIVIL_CHECKS_INIT: 'civilChecksInit',
    SEVEN_YEAR_CHECK_RUN: 'sevenYearCheckRun',
    GWC_CHECK_RUN: 'gwcCheckRun',
    SOC_CHECK_RUN: 'socCheckRun',
    FEDERAL_CIVIL_CHECK_RUN: 'federalCivilCheckRun',
    FEDERAL_CRIMINAL_CHECK_RUN: 'federalCriminalCheckRun',
    COUNTY_CRIMINAL_CHECK: 'countyCriminalCheckRun',
    COUNTY_CIVIL_CHECK_RUN: 'countyCivilCheckRun',
    BACKGROUND_CHECKS_TAB: 'backgroundChecks',
    MVR_CHECK_RUN: 'mvrCheckRun',
    EDUCATION_CHECK_RUN: 'educationCheckRun',
    EMPLOYMENT_CHECK_RUN: 'employmentCheckRun',
    PROFESSIONAL_LICENSE_CHECK_RUN: 'professionalLicenseCheckRun',
    EVENTS_TAB: 'events',
    REPORT_CONFIG: 'reportConfig',
    REPORT_GENERATE: 'reportGenerate',
};

// Generate Pdf Report constants

export const REPORT_STATUS = {
    INITIAL: 'Initial',
    IN_PROGRESS: 'In_progress',
    COMPLETED: 'Completed',
};

const REPORT_BUTTON_TEXTS = {
    [REPORT_STATUS.INITIAL]: 'Generate Report',
    [REPORT_STATUS.IN_PROGRESS]: 'Generating Report',
    [REPORT_STATUS.COMPLETED]: 'Generate Report',
};

const REPORT_MESSAGES = {
    INITIATED: 'We’re preparing your PDF report. Please keep this page open.',
    INITIATED_TABLET:
        'We’re preparing your PDF report. Please keep this page open. You can request a new download every 5 minutes.',
    GENERATED: 'Download Successful.',
    GENERATION_FAILED: 'Report generation failed. Please try after 5 mins.',
    CONFIG_FETCH_FAILED: "Couldn't load report options. Please try again.",
    DOWNLOAD_FAILED:
        "Couldn't download the report. Please check your email, we've sent it there too.",
};

const REPORT_ICON_SIZE = {
    width: '18px',
    height: '18px',
};

const REPORT_ICON_BASED_STATUS = {
    [REPORT_STATUS.INITIAL]: <DownloadIcon style={REPORT_ICON_SIZE} />,
    [REPORT_STATUS.IN_PROGRESS]: <CircularProgress size={15} />,
    [REPORT_STATUS.COMPLETED]: <DownloadIcon style={REPORT_ICON_SIZE} />,
};

export const userPdfReport = {
    REPORT_STATUS,
    REPORT_BUTTON_TEXTS,
    REPORT_MESSAGES,
    REPORT_ICON_BASED_STATUS,
};

export const PAGE_SIZE_OPTIONS = [
    { value: 'A4', label: 'A4 - Default', dimensions: '8.27" x 11.69"' },
    { value: 'Letter', label: 'Letter', dimensions: '8.5" x 11"' },
    { value: 'B1', label: 'B1', dimensions: '9.84" x 13.90"' },
    { value: 'A1', label: 'A1', dimensions: '23.4" x 33.1"' },
];

export const VERIFICATION_LABELS = {
    user_details: 'User Details',
    consent: 'Consent',
    user_indicator: 'User Indicator',
    govt_id: 'Government ID',
    kba: 'KBA',
    faov: 'Financial Account Ownership',
    risk_score: 'Risk Score',
    risk_insights: 'Risk Insights',
    seven_year: '7 Year Criminal Check',
    sex_offender_check: 'Sex Offender Check',
    county_criminal_check: 'County Criminal Check',
    county_civil_check: 'County Civil Check',
    federal_criminal_check: 'Federal Criminal Check',
    federal_civil_check: 'Federal Civil Check',
    global_watchlist: 'Global Watchlist',
    motor_vehicle_record: 'Motor Vehicle Records Verification',
    education: 'Education Verification',
    employment: 'Employment Verification',
    professional_license: 'Professional License Verification',
};

export const VERIFICATION_GROUP_LABELS = {
    identityVerification: 'Identity Verifications',
    insights: 'Insights',
    criminalChecks: 'Criminal Checks',
    backgroundChecks: 'Background Checks',
};

export const ORDERED_GROUPS = [
    'identityVerification',
    'insights',
    'criminalChecks',
    'backgroundChecks',
];

export const REPORT_CONFIG_TABS = [
    { id: 1, label: 'Privacy' },
    { id: 2, label: 'Verifications' },
    { id: 3, label: 'Page Orientation' },
];

export const ALWAYS_SELECTED_VERIFICATIONS = ['user_details', 'consent', 'user_indicator'];

export const reportGenerateSkeletonValues = {
    PRIVACY_ROW_COUNT: 3,
    VERIFICATION_ROW_COUNT: 8,
    MOBILE_TAB_COUNT: 3,
    MOBILE_ROW_COUNT: 6,
    commonSkeletonProps: {
        variant: 'rectangular',
        animation: 'wave',
    },
};
